"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonList,
  IonButton,
  IonGrid,
  IonRow,
  IonCol,
  IonLoading,
  IonToast,
  IonBadge,
} from "@ionic/react";
import {
  TextField,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  Chip,
  Button,
  Typography,
  Box,
  Paper,
  Divider,
  Avatar,
  Stack,
  Grid,
  DialogContent,
  Dialog,
  IconButton,
  DialogActions,
} from "@mui/material";
import {
  FilterAlt,
  Send,
  Mail,
  Call,
  Search,
  Clear,
  Visibility,
  GetApp,
  LocationOn,
  Work,
  CalendarToday,
  FilterList,
  Description,
} from "@mui/icons-material";
 import { useLocation, useHistory } from "react-router";
import { getFilterApplicants, processApplicants } from "../../../api/jobs/jobs";

const baseURL = "https://example.com"; // Declare baseURL here

interface Applicant {
  id: string;
  job_id: string;
  applicant_id: string;
  business_id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  age: number;
  yearsOfExperience: number;
  location: string;
  resume: string;
  message: string;
  status: string;
  test_sent: boolean;
  created_at: string;
}

const JobApplicantPage: React.FC = () => {
  // State for applicants and filtered applicants
  const location = useLocation();
  const history = useHistory();
  // Extract job ID from URL path
  const pathSegments = location.pathname.split("/");
  const jobId = pathSegments[pathSegments.length - 1];

  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [filteredApplicants, setFilteredApplicants] = useState<Applicant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(
    null
  );
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Filter states
  const [ageRange, setAgeRange] = useState<number[]>([20, 50]);
  const [experienceRange, setExperienceRange] = useState<number[]>([0, 10]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");

  // Available locations extracted from mock data
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);

  const getApplicants = async () => {
    try {
      const data = {
        job_id: jobId,
      };
      const response = await getFilterApplicants(data);
      console.log(response);
      setApplicants(response.data.data);

      setFilteredApplicants(response.data.data);
      console.log(response);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (applicants.length > 0) {
      const locations = [...new Set(applicants.map((app) => app.location))];
      setAvailableLocations(locations);
    }
  }, [applicants]);

  // Fetch applicants (mock data for example)
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      getApplicants();
      setLoading(false);
    }, 1000);
  }, []);

  // Apply filters
  const applyFilters = () => {
    const filtered = applicants.filter((applicant) => {
      // Age filter
      const ageMatch =
        applicant.age >= ageRange[0] && applicant.age <= ageRange[1];
      // Experience filter
      const expMatch =
        applicant.yearsOfExperience >= experienceRange[0] &&
        applicant.yearsOfExperience <= experienceRange[1];
      // Location filter
      const locationMatch =
        selectedLocations.length === 0 ||
        selectedLocations.includes(applicant.location);
      // Search text filter (name, email, or phone)
      const searchMatch =
        searchText === "" ||
        `${applicant.firstName} ${applicant.lastName}`
          .toLowerCase()
          .includes(searchText.toLowerCase()) ||
        applicant.email.toLowerCase().includes(searchText.toLowerCase()) ||
        applicant.phone.includes(searchText);

      return ageMatch && expMatch && locationMatch && searchMatch;
    });
    setFilteredApplicants(filtered);
  };

  // Reset filters
  const resetFilters = () => {
    setAgeRange([20, 50]);
    setExperienceRange([0, 10]);
    setSelectedLocations([]);
    setSearchText("");
    setFilteredApplicants(applicants);
  };

  // Send filtered results to endpoint
  const sendFilteredResults = async () => {
    setLoading(true);
    try {
      const data = {
        applicants: filteredApplicants,
        job_id: "12345",
        filters: {
          ageRange,
          experienceRange,
          locations: selectedLocations,
        },
      };
      const response = await processApplicants(data);
      console.log(response);
      setToastMessage(
        `Successfully sent ${filteredApplicants.length} filtered applicants`
      );
      setShowToast(true);
    } catch (error) {
      setToastMessage("Error sending filtered results");
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Hired":
        return "success";
      case "Rejected":
        return "danger";
      case "Interview":
        return "warning";
      default:
        return "primary";
    }
  };

  const handleViewDetails = (applicant: Applicant) => {
    setSelectedApplicant(applicant);
    setShowDetailsModal(true);
  };

  const handleResumeClick = (resumeUrl: string, applicantName: string) => {
    if (resumeUrl && resumeUrl.trim() !== "") {
      // If it's a full URL, open directly
      if (resumeUrl.startsWith("http")) {
        window.open(resumeUrl, "_blank");
      } else {
        // If it's a relative path, construct the full URL
        window.open(`${baseURL}/${resumeUrl}`, "_blank");
      }
    } else {
      setToastMessage(`Resume not available for ${applicantName}`);
      setShowToast(true);
    }
  };

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar
          color="primary"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <IonTitle style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
            Job Applicants Management
          </IonTitle>
          <IonButton
            slot="end"
            onClick={sendFilteredResults}
            disabled={loading}
            fill="solid"
            style={{
              "--background": "rgba(255, 255, 255, 0.2)",
              "--color": "white",
              "--border-radius": "12px",
              margin: "8px",
            }}
          >
            <Send style={{ marginRight: 8 }} />
            Send Results
          </IonButton>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen style={{ "--background": "#f8fafc" }}>
        {/* Filter Section */}
        <Paper
          elevation={8}
          sx={{
            m: 2,
            borderRadius: 3,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <FilterList sx={{ mr: 2, color: "primary.main", fontSize: 28 }} />
              <Typography variant="h5" fontWeight="bold" color="primary">
                Filter Applicants
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <IonGrid>
              <IonRow>
                <IonCol size="12" sizeMd="6">
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="600"
                      gutterBottom
                    >
                      Age Range: {ageRange[0]} - {ageRange[1]} years
                    </Typography>
                    <Slider
                      value={ageRange}
                      onChange={(e, newValue) =>
                        setAgeRange(newValue as number[])
                      }
                      valueLabelDisplay="auto"
                      min={18}
                      max={70}
                      sx={{
                        "& .MuiSlider-thumb": {
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        },
                        "& .MuiSlider-track": {
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        },
                      }}
                    />
                  </Box>
                </IonCol>

                <IonCol size="12" sizeMd="6">
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="600"
                      gutterBottom
                    >
                      Experience: {experienceRange[0]} - {experienceRange[1]}{" "}
                      years
                    </Typography>
                    <Slider
                      value={experienceRange}
                      onChange={(e, newValue) =>
                        setExperienceRange(newValue as number[])
                      }
                      valueLabelDisplay="auto"
                      min={0}
                      max={30}
                      sx={{
                        "& .MuiSlider-thumb": {
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        },
                        "& .MuiSlider-track": {
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        },
                      }}
                    />
                  </Box>
                </IonCol>

                <IonCol size="12" sizeMd="6">
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Locations</InputLabel>
                    <Select
                      multiple
                      value={selectedLocations}
                      onChange={(e) =>
                        setSelectedLocations(e.target.value as string[])
                      }
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {(selected as string[]).map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                              sx={{
                                background:
                                  "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: "white",
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      sx={{ borderRadius: 2 }}
                    >
                      {availableLocations.map((location) => (
                        <MenuItem key={location} value={location}>
                          <Checkbox
                            checked={selectedLocations.includes(location)}
                          />
                          <ListItemText primary={location} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </IonCol>

                <IonCol size="12" sizeMd="6">
                  <TextField
                    fullWidth
                    label="Search by name, email, or phone"
                    variant="outlined"
                    margin="normal"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Search sx={{ mr: 1, color: "action.active" }} />
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </IonCol>

                <IonCol size="12">
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="flex-end"
                    sx={{ mt: 2 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={resetFilters}
                      disabled={loading}
                      startIcon={<Clear />}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontWeight: "bold",
                      }}
                    >
                      Reset Filters
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<FilterAlt />}
                      onClick={applyFilters}
                      disabled={loading}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontWeight: "bold",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                        },
                      }}
                    >
                      Apply Filters
                    </Button>
                  </Stack>
                </IonCol>
              </IonRow>
            </IonGrid>
          </Box>
        </Paper>

        {/* Results Summary */}
        <Paper
          elevation={4}
          sx={{
            m: 2,
            borderRadius: 2,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Showing {filteredApplicants.length} of {applicants.length}{" "}
              applicants
            </Typography>
          </Box>
        </Paper>

        {/* Applicants List */}
        {filteredApplicants.length > 0 ? (
          <IonList style={{ background: "transparent" }}>
            {filteredApplicants.map((applicant) => (
              <Paper
                key={applicant.id}
                elevation={6}
                sx={{
                  m: 2,
                  borderRadius: 3,
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <Box sx={{ p: 3 }}>
                  <IonGrid>
                    <IonRow>
                      {/* Profile Section */}
                      <IonCol size="12" sizeMd="3">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: { xs: 2, md: 0 },
                          }}
                        >
                          <Avatar
                            src={`https://i.pravatar.cc/150?u=${applicant.email}`}
                            sx={{
                              width: 70,
                              height: 70,
                              mr: 2,
                              border: "3px solid",
                              borderColor: "primary.main",
                            }}
                          />
                          <Box>
                            <Typography variant="h6" fontWeight="bold">
                              {applicant.firstName} {applicant.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Age: {applicant.age}
                            </Typography>
                          </Box>
                        </Box>
                      </IonCol>

                      {/* Contact Info */}
                      <IonCol size="12" sizeMd="3">
                        <Stack spacing={1}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Mail
                              sx={{
                                mr: 1,
                                color: "primary.main",
                                fontSize: 20,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ wordBreak: "break-all" }}
                            >
                              {applicant.email}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Call
                              sx={{
                                mr: 1,
                                color: "primary.main",
                                fontSize: 20,
                              }}
                            />
                            <Typography variant="body2">
                              {applicant.phone}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <LocationOn
                              sx={{
                                mr: 1,
                                color: "primary.main",
                                fontSize: 20,
                              }}
                            />
                            <Typography variant="body2">
                              {applicant.location}
                            </Typography>
                          </Box>
                        </Stack>
                      </IonCol>

                      {/* Experience & Status */}
                      <IonCol size="12" sizeMd="3">
                        <Stack spacing={1}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Work
                              sx={{
                                mr: 1,
                                color: "primary.main",
                                fontSize: 20,
                              }}
                            />
                            <Typography variant="body2">
                              {applicant.yearsOfExperience} years experience
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <CalendarToday
                              sx={{
                                mr: 1,
                                color: "primary.main",
                                fontSize: 20,
                              }}
                            />
                            <Typography variant="body2">
                              Applied: {formatDate(applicant.created_at)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <IonBadge
                              color={getStatusColor(applicant.status)}
                              style={{
                                borderRadius: "12px",
                                padding: "4px 12px",
                                fontWeight: "bold",
                              }}
                            >
                              {applicant.status}
                            </IonBadge>
                            {applicant.test_sent && (
                              <IonBadge
                                color="medium"
                                style={{
                                  borderRadius: "12px",
                                  padding: "4px 12px",
                                }}
                              >
                                Test Sent
                              </IonBadge>
                            )}
                          </Box>
                        </Stack>
                      </IonCol>

                      {/* Actions */}
                      <IonCol size="12" sizeMd="3">
                        <Stack
                          direction={{ xs: "row", md: "column" }}
                          spacing={1}
                          justifyContent="flex-end"
                          alignItems="flex-end"
                        >
                          <Button
                            variant="outlined"
                            onClick={() =>
                              handleResumeClick(
                                applicant.resume,
                                `${applicant.firstName} ${applicant.lastName}`
                              )
                            }
                            startIcon={<GetApp />}
                            disabled={!applicant.resume}
                            sx={{
                              borderRadius: 2,
                              minWidth: "120px",
                              fontWeight: "bold",
                              opacity: applicant.resume ? 1 : 0.5,
                            }}
                          >
                            Resume
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<Visibility />}
                            onClick={() => handleViewDetails(applicant)}
                            sx={{
                              borderRadius: 2,
                              minWidth: "120px",
                              fontWeight: "bold",
                              background:
                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              "&:hover": {
                                background:
                                  "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                              },
                            }}
                          >
                            View Details
                          </Button>
                        </Stack>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </Box>
              </Paper>
            ))}
          </IonList>
        ) : (
          <Paper
            elevation={4}
            sx={{
              m: 2,
              borderRadius: 3,
              textAlign: "center",
              py: 6,
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No applicants match your filters
            </Typography>
            <Button
              variant="outlined"
              onClick={resetFilters}
              sx={{
                mt: 2,
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: "bold",
              }}
            >
              Reset Filters
            </Button>
          </Paper>
        )}

        {/* Applicant Details Modal */}
        {selectedApplicant && (
          <Dialog
            open={showDetailsModal}
            onClose={() => setShowDetailsModal(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 4,
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
                overflow: "scroll",
              },
            }}
          >
            <Box sx={{ position: "relative" }}>
              {/* Header with gradient background */}
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  p: 3,
                  position: "relative",
                }}
              >
                <IconButton
                  onClick={() => setShowDetailsModal(false)}
                  sx={{
                    position: "absolute",
                    right: 16,
                    top: 16,
                    color: "white",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    },
                  }}
                >
                  <Clear />
                </IconButton>

                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar
                    src={`https://i.pravatar.cc/150?u=${selectedApplicant.email}`}
                    sx={{
                      width: 80,
                      height: 80,
                      border: "3px solid rgba(255, 255, 255, 0.3)",
                      mr: 3,
                    }}
                  />
                  <Box>
                    <Typography variant="h4" fontWeight="700">
                      {selectedApplicant.firstName}{" "}
                      {selectedApplicant.middleName}{" "}
                      {selectedApplicant.lastName}
                    </Typography>
                    <Chip
                      label={selectedApplicant.status}
                      sx={{
                        mt: 1,
                        backgroundColor: getStatusColor(
                          selectedApplicant.status
                        ),
                        color: "white",
                        fontWeight: "600",
                        borderRadius: 2,
                        px: 1,
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <DialogContent sx={{ p: 0 }}>
                <Grid container>
                  {/* Left sidebar with contact info */}
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                      backgroundColor: "grey.50",
                      p: 3,
                      borderRight: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Stack spacing={3}>
                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight="600"
                          color="text.secondary"
                          gutterBottom
                        >
                          CONTACT INFORMATION
                        </Typography>
                        <Stack spacing={2}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Mail
                              sx={{
                                mr: 2,
                                color: "primary.main",
                                fontSize: 20,
                              }}
                            />
                            <Typography variant="body2">
                              {selectedApplicant.email}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Call
                              sx={{
                                mr: 2,
                                color: "primary.main",
                                fontSize: 20,
                              }}
                            />
                            <Typography variant="body2">
                              {selectedApplicant.phone}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <LocationOn
                              sx={{
                                mr: 2,
                                color: "primary.main",
                                fontSize: 20,
                              }}
                            />
                            <Typography variant="body2">
                              {selectedApplicant.location}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>

                      <Divider />

                      <Box>
                        <Typography
                          variant="subtitle1"
                          fontWeight="600"
                          color="text.secondary"
                          gutterBottom
                        >
                          PROFESSIONAL INFORMATION
                        </Typography>
                        <Stack spacing={1.5}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Age:
                            </Typography>
                            <Typography variant="body2" fontWeight="500">
                              {selectedApplicant.age} years
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Experience:
                            </Typography>
                            <Typography variant="body2" fontWeight="500">
                              {selectedApplicant.yearsOfExperience} years
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Applied:
                            </Typography>
                            <Typography variant="body2" fontWeight="500">
                              {formatDate(selectedApplicant.created_at)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Job ID:
                            </Typography>
                            <Typography variant="body2" fontWeight="500">
                              {selectedApplicant.job_id}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>

                      <Box sx={{ pt: 2 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() =>
                            handleResumeClick(
                              selectedApplicant.resume,
                              `${selectedApplicant.firstName} ${selectedApplicant.lastName}`
                            )
                          }
                          startIcon={<GetApp />}
                          disabled={!selectedApplicant.resume}
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            background:
                              "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #45a049 0%, #3d8b40 100%)",
                            },
                          }}
                        >
                          Download Resume
                        </Button>
                      </Box>
                    </Stack>
                  </Grid>

                  {/* Right content with cover letter */}
                  <Grid item xs={12} md={8} sx={{ p: 4 }}>
                    {selectedApplicant.message && (
                      <Box>
                        <Typography
                          variant="h6"
                          fontWeight="600"
                          gutterBottom
                          sx={{ mb: 3 }}
                        >
                          Cover Letter
                        </Typography>
                        <Paper
                          sx={{
                            p: 3,
                            backgroundColor: "grey.50",
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            maxHeight: "300px",
                            overflow: "auto",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                          >
                            {selectedApplicant.message}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {!selectedApplicant.message && (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 8,
                          color: "text.secondary",
                        }}
                      >
                        <Description
                          sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}
                        />
                        <Typography variant="h6" gutterBottom>
                          No Cover Letter
                        </Typography>
                        <Typography variant="body2">
                          This applicant didn't include a cover letter with
                          their application.
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions
                sx={{ p: 3, borderTop: "1px solid", borderColor: "divider" }}
              >
                <Button
                  onClick={() => setShowDetailsModal(false)}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1,
                    fontWeight: "600",
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </Box>
          </Dialog>
        )}

        {/* Modal Backdrop */}
        {showDetailsModal && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 9998,
            }}
            onClick={() => setShowDetailsModal(false)}
          />
        )}

        <IonLoading isOpen={loading} message="Loading..." />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
        />
      </IonContent>
    </IonPage>
  );
};

export default JobApplicantPage;
